import { Command as CommandPrimitive } from "cmdk";
import { Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Command, CommandInput, CommandList } from "@/components/ui/command";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

import { SearchResultsList } from "./SearchResultsList";
import { useGlobalSearch } from "./useGlobalSearch";

/**
 * Tracks the visual-viewport height so the mobile search modal can shrink to the
 * space *above* the on-screen keyboard. iOS Safari keeps `100vh`/fixed elements
 * at full screen height when the keyboard opens, so the modal otherwise extends
 * behind it — hiding the bottom rows and leaving nothing to scroll. Returns
 * `undefined` until measured or when the API is unavailable (no override then).
 */
function useVisualViewportHeight(): number | undefined {
  const [height, setHeight] = useState<number | undefined>(undefined);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setHeight(vv.height);
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);
  return height;
}

/**
 * Top-bar global search, built on the cmdk `Command` primitive (keyboard nav,
 * selection, and listbox/option a11y come from cmdk). `shouldFilter={false}` —
 * results come from the server (`useSearch`), not cmdk's client-side filter.
 *
 * Desktop (md+): an inline input with a floating results dropdown.
 * Mobile (<md): a search icon opens a full-screen Sheet — input pinned at the
 * top (above the keyboard), results scrolling below. A contact deep-links to
 * its customer; the per-kind route mirrors `resolve_entity_route` server-side.
 *
 * Market Intelligence destinations ("np15 curve") join the server's records,
 * computed in the browser by `useMarketSearch` — see `market-search.ts`.
 */
export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const desktopRef = useRef<HTMLDivElement>(null);
  const viewportHeight = useVisualViewportHeight();
  const search = useGlobalSearch(() => {
    setOpen(false);
    setMobileOpen(false);
  });
  const { raw, setRaw, ready, marketHits, reportHits, marketEnabled } = search;

  // Close the desktop dropdown on outside click. (The mobile Sheet manages its
  // own dismissal.)
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (desktopRef.current && !desktopRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const renderList = (columns: boolean) => (
    <SearchResultsList
      results={search.results}
      marketHits={marketHits}
      reportHits={reportHits}
      columns={columns}
      settledEmpty={search.settledEmpty}
      query={search.query}
      onSelect={search.selectRecord}
      onSelectMarket={search.selectMarket}
      onSelectReport={search.selectReport}
    />
  );
  // Two columns need twice the room; records alone keep the original width.
  const twoColumns = marketHits.length > 0 || reportHits.length > 0;

  return (
    <>
      {/* Desktop: inline input + floating dropdown. Command is just a
          positioning context here (its card chrome is stripped). */}
      <div ref={desktopRef} className="relative hidden md:block">
        <Command
          shouldFilter={false}
          className="h-auto w-[260px] overflow-visible rounded-none border-0 bg-transparent"
        >
          {/* Original pill look: cmdk's raw input (keeps keyboard nav) styled
              ourselves, since the repo's CommandInput forces an icon + border. */}
          <div className="relative">
            <Search
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-dim"
            />
            <CommandPrimitive.Input
              value={raw}
              onValueChange={(v) => {
                setRaw(v);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setOpen(false);
                  e.currentTarget.blur();
                }
              }}
              placeholder="Search…"
              className="h-8 w-[260px] rounded-md border border-border bg-input pl-7 pr-2 text-sm text-foreground placeholder:text-text-dim focus:border-primary-mid focus:outline-none"
            />
          </div>
          {open && ready && (
            <CommandList
              className={cn(
                "absolute right-0 top-full z-40 mt-1 max-h-none rounded-md border border-border bg-popover shadow-lg",
                twoColumns ? "w-[min(800px,calc(100vw-2rem))]" : "w-[400px]",
              )}
            >
              {renderList(true)}
            </CommandList>
          )}
        </Command>
      </div>

      {/* Mobile: icon button opens a full-screen search modal. */}
      <button
        type="button"
        aria-label="Search"
        onClick={() => setMobileOpen(true)}
        className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-row-hover hover:text-foreground md:hidden"
      >
        <Search size={16} />
      </button>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        {/* maxHeight = visual viewport so the modal sits above the keyboard. */}
        <SheetContent style={viewportHeight ? { maxHeight: viewportHeight } : undefined}>
          <Command
            shouldFilter={false}
            className="h-full w-full overflow-hidden rounded-none border-0 bg-transparent"
          >
            {/* Header bar — at the very top, above the input; the sheet's
                built-in ✕ (absolute top-3 right-3) lands here. */}
            <div className="flex h-12 shrink-0 items-center border-b border-border px-4">
              <SheetTitle className="text-sm font-semibold text-foreground">Search</SheetTitle>
            </div>
            {/* Input row with a clear button. text-base (16px) keeps iOS from
                zooming on focus. */}
            <div className="relative shrink-0">
              <CommandInput
                value={raw}
                onValueChange={setRaw}
                placeholder={
                  marketEnabled
                    ? "Search clients, deals, accounts, market points…"
                    : "Search clients, deals, contacts, accounts…"
                }
                className="h-12 pr-10 text-base"
              />
              {raw && (
                <button
                  type="button"
                  aria-label="Clear search"
                  // preventDefault keeps the input focused so typing can continue.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    search.clear();
                  }}
                  className="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-text-dim hover:bg-row-hover hover:text-foreground"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            {/* Results — min-h-0 lets this flex child shrink so it scrolls. */}
            <CommandList className="scrollbar-thin min-h-0 max-h-none flex-1 overflow-y-auto pb-4">
              {ready ? (
                renderList(false)
              ) : (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {marketEnabled
                    ? "Type to search clients, deals, contacts, account numbers, or market points."
                    : "Type to search clients, deals, contacts, and account numbers."}
                </div>
              )}
            </CommandList>
          </Command>
        </SheetContent>
      </Sheet>
    </>
  );
}
